import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Rating } from 'primereact/rating';
import { PriorityTag, StatusTag, StatCard } from './components/helper';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../services/store/userSlice';
import { toast } from 'react-toastify';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { supportApi } from '../../services';


const fmtDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy}, ${hh}:${min}:${ss}`;
};

const AgentQueue = () => {
    const navigate = useNavigate();
    const currentUser = useSelector(selectUser);
    const agentId = currentUser?.id;
    const [queueTickets, setQueueTickets] = useState([]);
    const [resolvedTickets, setResolvedTickets] = useState([]);
    const [allResolvedTickets, setAllResolvedTickets] = useState([]);
    const [stats, setStats] = useState({ open: 0, breaching: 0, resolvedToday: 0, csat: '—' });
    const [loading, setLoading] = useState(true);
    const [confirmResolveId, setConfirmResolveId] = useState(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    useEffect(() => {
        if (!agentId) return;
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        Promise.all([
            supportApi.getAllTickets({ assignedTo: agentId, pageSize: 200 }),
            supportApi.getAllTickets({ assignedTo: agentId, pageSize: 1000 }),
            supportApi.getSeniorInbox(),
        ]).then(([openRes, allRes, inboxRes]) => {
            const allAssigned = Array.isArray(openRes.data?.data) ? openRes.data.data : [];
            const open = allAssigned.filter(t => !['Resolved', 'Closed'].includes(t.status));
            const all = Array.isArray(allRes.data?.data) ? allRes.data.data : [];
            const resolvedAndClosed = all.filter(t => ['Resolved', 'Closed'].includes(t.status));
            const resolvedAndClosedToday = resolvedAndClosed.filter(t => {
                const date = t.resolvedAt ?? t.closedAt ?? t.updatedAt;
                return date && date.startsWith(today);
            });
            // Merge senior inbox (unassigned watcher tickets) into the queue with a flag
            const inbox = Array.isArray(inboxRes.data?.data) ? inboxRes.data.data : [];
            const inboxIds = new Set(inbox.map(t => t.id));
            const combined = [
                ...inbox.map(t => ({ ...t, _pendingTriage: true })),
                ...open.filter(t => !inboxIds.has(t.id)),
            ];

            setQueueTickets(combined);
            setResolvedTickets(resolvedAndClosedToday);
            setAllResolvedTickets(resolvedAndClosed);
            const csatRows = resolvedAndClosed.filter(t => t.csatRating != null);
            const avgCsat = csatRows.length
                ? (csatRows.reduce((s, t) => s + t.csatRating, 0) / csatRows.length).toFixed(1)
                : '—';
            setStats({
                open: combined.length,
                breaching: combined.filter(t => t.slaBreach).length,
                resolvedToday: resolvedAndClosedToday.length,
                csat: avgCsat !== '—' ? `${avgCsat}/5` : '—',
            });
        }).catch(() => toast.error('Failed to load queue'))
          .finally(() => setLoading(false));
    }, [agentId]);

    const onClose = async (ticketId) => {
        try {
            await supportApi.closeTicket(ticketId);
            setQueueTickets(p => p.map(t => t.id === ticketId ? { ...t, status: 'Closed' } : t));
            setResolvedTickets(p => p.map(t => t.id === ticketId ? { ...t, status: 'Closed' } : t));
            setAllResolvedTickets(p => p.map(t => t.id === ticketId ? { ...t, status: 'Closed' } : t));
            toast.success('Ticket closed');
        } catch {
            toast.error('Failed to close ticket');
        }
    };

    const onResolve = async (ticketId) => {
        try {
            await supportApi.resolveTicket(ticketId);
            const resolved = queueTickets.find(t => t.id === ticketId);
            const newQueue = queueTickets.filter(t => t.id !== ticketId);
            const newResolved = resolved ? [...resolvedTickets, { ...resolved, status: 'Resolved', resolvedAt: new Date().toISOString() }] : resolvedTickets;
            setQueueTickets(newQueue);
            setResolvedTickets(newResolved);
            const csatRows = newResolved.filter(t => t.csatRating != null);
            const avgCsat = csatRows.length
                ? (csatRows.reduce((s, t) => s + t.csatRating, 0) / csatRows.length).toFixed(1)
                : '—';
            setStats({
                open: newQueue.length,
                breaching: newQueue.filter(t => t.slaBreach).length,
                resolvedToday: newResolved.length,
                csat: avgCsat !== '—' ? `${avgCsat}/5` : '—',
            });
            toast.success('Ticket resolved');
        } catch {
            toast.error('Failed to resolve ticket');
        }
    };

    return (
        <div>
            <h1 className="ess-page-title">My Support Queue</h1>
            <p className="ess-page-sub">Showing tickets assigned to you</p>

            <div className="stat-grid stat-grid-4" style={{ marginBottom: 20 }}>
                <StatCard num={stats.open} label="Open in Queue" colorClass="sc-orange" />
                <StatCard num={stats.breaching} label="Breaching SLA" colorClass="sc-red" />
                <StatCard num={stats.resolvedToday} label="Resolved Today" colorClass="sc-green" />
                <StatCard num={stats.csat} label="CSAT Score" colorClass="sc-blue" />
            </div>

            <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>

                <TabPanel header={`My Queue (${queueTickets.length})`}>
                    <div className="grid-3" style={{ marginBottom: 20 }}>
                        {queueTickets.slice(0, 6).map((q, i) => (
                            <div className="q-card" key={i} style={{ borderLeftColor: q.slaBreach ? 'var(--red)' : 'var(--blue)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                    <div className="q-id">
                                        {q.ticketNo}
                                        {q.slaBreach && <span style={{ color: 'var(--red)', marginLeft: 6 }}>⚠ SLA BREACH RISK</span>}
                                    </div>
                                    <StatusTag value={q.status} />
                                </div>
                                <div className="q-title">{q.subject}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                    <PriorityTag value={q.priority} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <Avatar label={(q.requester ? `${q.requester.firstName} ${q.requester.lastName}` : (q.raisedBy ?? '')).substring(0, 2).toUpperCase()} shape="circle" size="small"
                                            style={{ background: 'var(--blue2)', color: '#fff', fontSize: 9, width: 20, height: 20 }} />
                                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{q.requester ? `${q.requester.firstName} ${q.requester.lastName}` : (q.raisedBy ?? '—')}</span>
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: q.slaBreach ? 'var(--red)' : (q.slaPct ?? 0) >= 80 ? 'var(--orange)' : 'var(--green)' }}>
                                        {q.slaBreach ? '⚠ ' : (q.slaPct ?? 0) >= 80 ? '⏰ ' : '✓ '}{q.slaRemaining ?? '—'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <DataTable value={Array.isArray(queueTickets) ? queueTickets : []} loading={loading}
                        rowClassName={r => r._pendingTriage ? 'watcher-row' : r.slaBreach ? 'sla-breach' : ''}
                        onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}`)}
                        style={{ cursor: 'pointer' }}>
                        <Column field="ticketNo" header="Ticket ID" body={r => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <strong style={{ color: r.slaBreach ? 'var(--red)' : 'var(--blue)' }}>{r.ticketNo}</strong>
                                {r._pendingTriage && (
                                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fff7e6', color: 'var(--orange)', border: '1px solid var(--orange)', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                                        Pending Triage
                                    </span>
                                )}
                            </div>
                        )} style={{ width: 210 }} />
                        <Column field="subject" header="Subject" style={{ minWidth: 200 }} />
                        <Column field="raisedBy" header="Requester" body={r => r.requester ? `${r.requester.firstName} ${r.requester.lastName}` : (r.raisedBy ?? '—')} />
                        <Column field="category" header="Category" body={r => r.category?.name ?? r.category ?? '—'} />
                        <Column field="priority" header="Priority" body={r => <PriorityTag value={r.priority} />} />
                        <Column field="status" header="Status" body={r => <StatusTag value={r.status} />} />
                        <Column field="slaRemaining" header="SLA" body={r => {
                                const breached = r.slaBreach;
                                const pct = r.slaPct ?? 0;
                                const color = breached ? 'var(--red)' : pct >= 80 ? 'var(--orange)' : 'var(--green)';
                                return <span style={{ color, fontWeight: 700, fontSize: 12 }}>{breached ? '⚠ ' : pct >= 80 ? '⏰ ' : '✓ '}{r.slaRemaining ?? '—'}</span>;
                            }} />
                        <Column field="createdAt" header="Raised On" body={r => fmtDate(r.createdAt)} />
                        <Column field="updatedAt" header="Last Updated" body={r => fmtDate(r.updatedAt)} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="Escalated">
                    <Message severity="warn" text="Escalated tickets are awaiting senior review." style={{ marginBottom: 16 }} />
                    <DataTable value={Array.isArray(queueTickets) ? queueTickets.filter(t => t.priority === 'Critical' || t.priority === 'High') : []} rowClassName={r => r.slaBreach ? 'sla-breach' : ''}
                        onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}`)}
                        style={{ cursor: 'pointer' }}>
                        <Column field="ticketNo" header="Ticket ID" body={r => <strong style={{ color: 'var(--red)' }}>{r.ticketNo}</strong>} />
                        <Column field="subject" header="Subject" />
                        <Column field="priority" header="Priority" body={r => <PriorityTag value={r.priority} />} />
                        <Column field="status" header="Status" body={r => <StatusTag value={r.status} />} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={`Resolved & Closed Today (${resolvedTickets.length})`}>
                    <DataTable value={Array.isArray(resolvedTickets) ? resolvedTickets : []}
                        onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}`)}
                        style={{ cursor: 'pointer' }}>
                        <Column field="ticketNo" header="Ticket ID" body={r => <strong style={{ color: 'var(--blue)' }}>{r.ticketNo}</strong>} />
                        <Column field="subject" header="Subject" />
                        <Column field="priority" header="Priority" body={r => <PriorityTag value={r.priority} />} />
                        <Column field="status" header="Status" body={r => <StatusTag value={r.status} />} />
                        <Column header="CSAT" body={r => <Rating value={r.csatRating ?? 4} readOnly cancel={false} />} />
                        <Column header="Resolution Time" body={r => {
                            let display = r.resolutionTime;
                            if (!display && r.resolvedAt && r.createdAt) {
                                const diffMs = new Date(r.resolvedAt) - new Date(r.createdAt);
                                const totalMins = Math.floor(diffMs / 60000);
                                const hrs = Math.floor(totalMins / 60);
                                const mins = totalMins % 60;
                                display = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                            }
                            return <span style={{ color: 'var(--green)', fontWeight: 600 }}>{display ?? '—'}</span>;
                        }} />
                        <Column header="Resolved/Closed On" body={r => {
                            const date = r.closedAt ?? r.resolvedAt;
                            return date ? new Date(date).toLocaleString() : '—';
                        }} />
                    </DataTable>
                </TabPanel>

                <TabPanel header={`Resolved & Closed (${allResolvedTickets.length})`}>
                    <DataTable value={allResolvedTickets} loading={loading} paginator rows={10} paginatorTemplate="PrevPageLink PageLinks NextPageLink"
                        onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}`)}
                        style={{ cursor: 'pointer' }}>
                        <Column field="ticketNo" header="Ticket ID" body={r => <strong style={{ color: 'var(--blue)' }}>{r.ticketNo}</strong>} style={{ width: 130 }} />
                        <Column field="subject" header="Subject" style={{ minWidth: 180 }} />
                        <Column field="raisedBy" header="Requester" body={r => r.requester ? `${r.requester.firstName} ${r.requester.lastName}` : (r.raisedBy ?? '—')} />
                        <Column field="category" header="Category" body={r => r.category?.name ?? r.category ?? '—'} />
                        <Column field="priority" header="Priority" body={r => <PriorityTag value={r.priority} />} />
                        <Column field="status" header="Status" body={r => <StatusTag value={r.status} />} />
                        <Column header="CSAT" body={r => r.csatRating ? <Rating value={r.csatRating} readOnly cancel={false} /> : <span style={{ color: 'var(--muted)' }}>—</span>} />
                        <Column header="Resolution Time" body={r => {
                            let display = r.resolutionTime;
                            if (!display && r.resolvedAt && r.createdAt) {
                                const diffMs = new Date(r.resolvedAt) - new Date(r.createdAt);
                                const totalMins = Math.floor(diffMs / 60000);
                                const hrs = Math.floor(totalMins / 60);
                                const mins = totalMins % 60;
                                display = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                            }
                            return <span style={{ color: 'var(--green)', fontWeight: 600 }}>{display ?? '—'}</span>;
                        }} />
                        <Column header="Resolved/Closed On" body={r => {
                            const date = r.closedAt ?? r.resolvedAt;
                            return date ? new Date(date).toLocaleDateString() : '—';
                        }} />
                    </DataTable>
                </TabPanel>
            </TabView>
                   <Dialog header="Confirm Resolve" visible={!!confirmResolveId} style={{ width: 380 }} onHide={() => setConfirmResolveId(null)}>
                       <div style={{ fontSize: 13, marginBottom: 20 }}>
                           Are you sure you want to mark this ticket as <strong>Resolved</strong>?
                       </div>
                       <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                           <Button label="Cancel" outlined severity="secondary" onClick={() => setConfirmResolveId(null)} />
                           <Button label="Resolve" icon="pi pi-check" style={{ background: 'var(--green)', border: 'none' }}
                               onClick={() => { onResolve(confirmResolveId); setConfirmResolveId(null); }} />
                       </div>
                   </Dialog>
               </div>
           );
       };

       export default AgentQueue;

